import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { JSONSchema7 } from "json-schema"
import type { UiSchema } from "@rjsf/core"
import { SchemaForm } from "../../../../Lazy/SchemaForm"
import type { SchemaFormRefProps } from "resha"
import {
    JobSubmitData,
    fromInputs
} from "../../../../../../Models/Client/JobSubmitData"
import { useUrlSignerFromContext } from "../../../../../../Hooks/useUrlSigner"

interface InputSchemaFormProps {
    data?: JobSubmitData
    schema: JSONSchema7
    uiSchema?: UiSchema
    renderExamples?: boolean
    testId?: string
    single: boolean
    shouldRenderFieldInPanel?: boolean
    blobUploader: (blob: Blob, name?: string) => Promise<string>
    onChange: (data: JobSubmitData, errors?: unknown[]) => void
    onSubmit: (
        data: JobSubmitData,
        errors?: unknown[],
        canSubmit?: boolean
    ) => void
    initiateSubmit: boolean
    stopSubmit: () => void
}
export const InputSchemaForm: FC<InputSchemaFormProps> = ({
    testId,
    data,
    schema,
    uiSchema,
    shouldRenderFieldInPanel,
    single,
    initiateSubmit,
    blobUploader,
    onChange,
    onSubmit,
    stopSubmit
}) => {
    const urlSigner = useUrlSignerFromContext()
    const [liveValidate, setLiveValidation] = useState(false)
    const schemaRef = useRef<SchemaFormRefProps>(null)
    // this helps in using one schema form
    const { arraySchema, arrayUiSchema } = useMemo<{
        arraySchema: JSONSchema7
        arrayUiSchema: UiSchema
    }>(
        () => ({
            arraySchema: {
                type: "array",
                items: {
                    ...schema,
                    definitions: undefined
                },
                definitions: schema.definitions,
                minItems: 1,
                ...(single && {
                    maxItems: 1
                })
            },
            arrayUiSchema: { items: uiSchema }
        }),
        [schema, uiSchema, single]
    )

    const handleSubmit = useCallback(
        (formData: unknown) => {
            if (!Array.isArray(formData)) {
                return
            }

            onSubmit(fromInputs(formData, single), [], true)
        },
        [onSubmit, single]
    )

    const handleChange = useCallback(
        (formData: unknown) => {
            if (!Array.isArray(formData)) {
                return
            }

            onChange(fromInputs(formData, single), [])
        },
        [onChange, single]
    )

    useEffect(() => {
        if (initiateSubmit) {
            schemaRef.current?.submit()
            setLiveValidation(true)
        }
    }, [initiateSubmit])

    return (
        <div data-testid={testId} className="p-4 ">
            <SchemaForm
                ref={schemaRef}
                className="flex-grow"
                schema={arraySchema}
                uiSchema={arrayUiSchema}
                formData={
                    data?.type === "raw"
                        ? data.jobs.map(({ input }) => input)
                        : undefined
                }
                urlSigner={urlSigner}
                blobUploader={blobUploader}
                shouldRenderFieldInPanel={shouldRenderFieldInPanel}
                liveValidate={liveValidate}
                skipMissingDataField={false}
                enableImageTools={true}
                onChange={handleChange}
                onSubmit={handleSubmit}
                onError={stopSubmit}
                extraError={true}
            />
        </div>
    )
}
